import type { StatusTone } from "./care/residents";

/** One status word, one colour, one spelling for statuses with no vocabulary of their own. An
 * unknown status falls to neutral and its title-cased self instead of throwing. Richer vocabularies
 * (`residentStatusLabel`, `ATS_STATUS_LABELS`) win where they apply. */
const TONE_BY_STATUS: Record<string, StatusTone> = {
  active: "ok",
  current: "ok",
  completed: "ok",
  complete: "ok",
  paid: "ok",
  approved: "ok",
  accepted: "ok",
  signed: "ok",
  administered: "ok",
  delivered: "ok",
  received: "ok",

  pending: "info",
  draft: "info",
  sent: "info",
  scheduled: "info",
  submitted: "info",
  /* Waiting on a reviewer, as "submitted" is: not yet good, not a problem. */
  pending_review: "info",
  in_progress: "info",
  queued: "info",
  processing: "info",
  sending: "info",
  receiving: "info",
  "in-progress": "info",
  move_in: "info",
  "move-in": "info",
  prospective: "info",
  lead: "info",
  out_of_facility_active: "info",

  hospital: "warn",
  out_of_facility_hold: "warn",
  on_leave: "warn",
  "on-leave": "warn",
  respite: "warn",
  partial: "warn",
  overdue: "warn",
  expiring: "warn",
  discharged: "warn",
  missed: "warn",
  /* A required document nobody has provided: due, not yet a failure. */
  missing: "warn",

  critical: "crit",
  failed: "crit",
  rejected: "crit",
  expired: "crit",
  /* The old schema wrote "voided"; this one writes "void". Both, so the tone
     does not depend on which spelling a row was written under. */
  voided: "crit",
  void: "crit",
  refused: "crit",
  declined: "crit",
  revoked: "crit",

  inactive: "neutral",
  archived: "neutral",
  cancelled: "neutral",
  canceled: "neutral",
  unknown: "neutral",
  /* Deliberately excused, so neither owed nor done. */
  waived: "neutral",
};

export function statusTone(status: string | null | undefined): StatusTone {
  if (!status) return "neutral";
  return TONE_BY_STATUS[status.toLowerCase().trim()] ?? "neutral";
}

/** Severity and priority words: the second mapping, kept one table per job. */
const TONE_BY_SEVERITY: Record<string, StatusTone> = {
  critical: "crit",
  urgent: "crit",
  severe: "crit",

  high: "warn",
  medium: "warn",
  moderate: "warn",

  normal: "info",

  low: "neutral",
  minor: "neutral",
};

export function severityTone(severity: string | null | undefined): StatusTone {
  if (!severity) return "neutral";
  return TONE_BY_SEVERITY[severity.toLowerCase().trim()] ?? "neutral";
}

/* A few statuses do not read well however they are cased. "undelivered" is a
   double negative to scan past when the thing you need to know is that it did
   not arrive. */
const LABEL_BY_STATUS: Record<string, string> = {
  undelivered: "Not delivered",
  no_answer: "No answer",
  in_progress: "In progress",
};

/** Title-cases any stored code for display ("move_in" is "Move in"). Separate from `statusLabel`
 * because the neighbouring columns hold codes that are not states. */
export function codeLabel(code: string | null | undefined, fallback = "-"): string {
  if (!code) return fallback;
  const words = code.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Title-cases a raw status for display ("move_in" -> "Move in"). */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return "Unknown";
  return LABEL_BY_STATUS[status.toLowerCase().trim()] ?? codeLabel(status, "Unknown");
}
